import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SEO_ROUTES } from "../../config/seo-routes";
import { getSchemas, type PageData } from "../../config/schemas";

interface Breadcrumb {
  name: string;
  path?: string;
}

interface SchemaOrg {
  type: "LocalBusiness" | "Service" | "FAQPage" | "WebPage" | "BreadcrumbList";
  jsonLd?: Record<string, unknown>;
}

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  robots?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  breadcrumbs?: Breadcrumb[];
  schema?: SchemaOrg | SchemaOrg[];
  noHreflang?: boolean;
  faqs?: Array<{ q: string; a: string }>;
  offers?: Array<{ name: string; price: string; priceCurrency?: string }>;
}

const SITE_URL = "https://persianas-toldos-malaga.es";
const DEFAULT_OG = "https://persianas-toldos-malaga.es/og/og-default.jpg";

export function generateBreadcrumbSchema(breadcrumbs: Breadcrumb[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": crumb.name,
      ...(crumb.path ? { "item": `${SITE_URL}${crumb.path}` } : {}),
    })),
  };
}

export function generateServiceSchema(serviceType: string, url: string, description: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "provider": { "@id": `${SITE_URL}/#business` },
    "serviceType": serviceType,
    "url": `${SITE_URL}${url}`,
    "description": description,
    "areaServed": {
      "@type": "City",
      "name": "Málaga",
      "containedInPlace": { "@type": "Country", "name": "España" },
    },
  };
}

export function generateServiceOfferCatalogSchema(
  serviceType: string,
  url: string,
  description: string,
  offers: Array<{ name: string; price: string; priceCurrency?: string }>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceType,
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "name": "Persianas Toldos Málaga",
    },
    "areaServed": {
      "@type": "State",
      "name": "Málaga",
    },
    "url": `${SITE_URL}${url}`,
    "description": description,
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Servicios de ${serviceType.toLowerCase()}`,
      "itemListElement": offers.map((offer) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": offer.name,
        },
        "price": offer.price,
        "priceCurrency": offer.priceCurrency || "EUR",
      })),
    },
  };
}

export function generateFAQPageSchema(questions: Array<{ q: string; a: string }>): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a,
      },
    })),
  };
}

export function SEO({
  title,
  description,
  canonical,
  robots = "index, follow, max-image-preview:large",
  ogImage = DEFAULT_OG,
  ogType = "website",
  breadcrumbs,
  schema,
  noHreflang = false,
  faqs,
  offers,
}: SEOProps) {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const routeConfig = SEO_ROUTES[normalizedPath];

  const finalTitle = routeConfig?.title ?? title ?? "";
  const finalDescription = routeConfig?.description ?? description ?? "";
  let rawCanonical = routeConfig?.canonical ?? canonical ?? (normalizedPath === "/" ? "/" : normalizedPath + "/");
  if (rawCanonical !== "/" && !rawCanonical.endsWith("/")) {
    rawCanonical += "/";
  }
  const finalCanonical = rawCanonical;

  const fullUrl = finalCanonical.startsWith("http") ? finalCanonical : `${SITE_URL}${finalCanonical}`;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

  useEffect(() => {
    // Title
    document.title = finalTitle;

    // Meta helpers
    const setMeta = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        if (property) el.setAttribute("property", name);
        else el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const setLink = (rel: string, href: string, attrs?: Record<string, string>) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
      if (attrs) {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
    };

    // Hreflang: always remove old alternates and recreate both to avoid collisions
    document.querySelectorAll('link[rel="alternate"]').forEach((el) => el.remove());

    setMeta("description", finalDescription);
    setMeta("robots", robots);
    setMeta("author", "Persianas Toldos Málaga");
    setMeta("language", "Spanish");

    setMeta("og:title", finalTitle, true);
    setMeta("og:description", finalDescription, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", fullUrl, true);
    setMeta("og:image", fullOgImage, true);
    setMeta("og:locale", "es_ES", true);
    setMeta("og:site_name", "Persianas Toldos Málaga", true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", finalTitle);
    setMeta("twitter:description", finalDescription);
    setMeta("twitter:image", fullOgImage);

    setLink("canonical", fullUrl);

    if (!noHreflang) {
      const linkEs = document.createElement("link");
      linkEs.rel = "alternate";
      linkEs.hreflang = "es-ES";
      linkEs.href = fullUrl;
      document.head.appendChild(linkEs);

      const linkDefault = document.createElement("link");
      linkDefault.rel = "alternate";
      linkDefault.hreflang = "x-default";
      linkDefault.href = fullUrl;
      document.head.appendChild(linkDefault);
    }

    // JSON-LD schemas via config centralizada
    const pageData: PageData = {
      breadcrumbs,
      title: finalTitle,
      description: finalDescription,
      faqs,
      offers,
    };
    const schemas = getSchemas(normalizedPath, pageData);

    // Merge schemas manuales pasados como prop (para casos especiales como FAQ inline)
    if (schema) {
      const arr = Array.isArray(schema) ? schema : [schema];
      arr.forEach((s) => {
        if (s.jsonLd) schemas.push(s.jsonLd);
      });
    }

    // Remove old JSON-LD scripts
    document.querySelectorAll('script[data-seo="schema"]').forEach((el) => el.remove());

    schemas.forEach((s) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "schema");
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo="schema"]').forEach((el) => el.remove());
      document.querySelectorAll('link[rel="alternate"]').forEach((el) => el.remove());
    };
  }, [finalTitle, finalDescription, fullUrl, robots, fullOgImage, ogType, breadcrumbs, schema, noHreflang, normalizedPath, faqs, offers]);

  return null;
}

export default SEO;